import { Button } from "@/components/ui/button";
import { FilteredSelectLocation } from "@/components/ui/filtered-select-location-2";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CustomPopover } from "@/components/ui/popover-custom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/use-toast";
import { useLocationContext } from "@/contexts/location-context";
import { buscarCep } from "@/services/flowQuote";
import { formatCEP } from "@/utils/format";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { PaymentFormValues } from "./payment-types";

interface AddressFieldsProps {
  coletaCep?: string | null;
  coletaEndereco?: string | null;
  coletaNumero?: string | null;
  coletaComplemento?: string | null;
  coletaBairro?: string | null;
  coletaCidade?: string | null;
  entregaCep?: string | null;
  entregaEndereco?: string | null;
  entregaNumero?: string | null;
  entregaComplemento?: string | null;
  entregaBairro?: string | null;
  entregaCidade?: string | null;
}

export default function AddressFields({
  coletaCep,
  entregaCep,
}: AddressFieldsProps) {
  const form = useFormContext<PaymentFormValues>();
  const { locations } = useLocationContext();
  const [loading, setLoading] = useState(false);
  const [addressSource, setAddressSource] = useState<
    "coleta" | "entrega" | null
  >(null);

  const [lastSearchedCep, setLastSearchedCep] = useState<string>("");
  const isAutoSearchingRef = useRef(false);

  const cepValue = form.watch("address.cep");

  const handleSearchCep = useCallback(async () => {
    const cep = form.getValues("address.cep");
    if (!cep) {
      toast({
        title: "CEP não informado",
        description: "Por favor, informe o CEP para buscar o endereço",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const result = await buscarCep(cep.replace("-", ""));

      if (result && result[0] && result[1]) {
        const cepData = result[0];
        const municipio = result[1];

        form.setValue("address.logradouro", cepData.logradouro || "");
        form.setValue("address.bairro", cepData.bairro || "");
        form.setValue("address.cidade", cepData.localidade || "");
        form.setValue("address.estado", cepData.uf || "");
        form.setValue("address.mun_codigo", municipio.mun_codigo || "");
        form.setValue("address.cep_pesquisado", cep);

        setLastSearchedCep(cep);

        toast({
          title: "Endereço encontrado",
          description: "Os dados foram preenchidos automaticamente",
        });
      } else {
        toast({
          title: "CEP não encontrado",
          description: "Verifique o CEP informado e tente novamente",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erro ao buscar CEP",
        description: "Não foi possível buscar o endereço. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [form]);

  const handleSearchCepRef = useRef(handleSearchCep);
  handleSearchCepRef.current = handleSearchCep;

  useEffect(() => {
    const cleanCep = cepValue?.replace(/\D/g, "");

    if (
      cleanCep?.length === 8 &&
      cepValue !== lastSearchedCep &&
      !loading &&
      !isAutoSearchingRef.current
    ) {
      isAutoSearchingRef.current = true;
      const timer = setTimeout(async () => {
        await handleSearchCepRef.current();
        isAutoSearchingRef.current = false;
      }, 500);

      return () => {
        clearTimeout(timer);
        isAutoSearchingRef.current = false;
      };
    }
  }, [cepValue, lastSearchedCep, loading]);

  const handleAddressSourceChange = async (value: string) => {
    const source = value as "coleta" | "entrega";
    setAddressSource(source);

    const cepValue = source === "coleta" ? coletaCep : entregaCep;

    if (cepValue) {
      form.setValue("address.cep", formatCEP(cepValue));
      await handleSearchCep();
    }
  };

  const showRadioButtons = !!coletaCep || !!entregaCep;

  return (
    <div className="flex flex-col gap-3 mb-3">
      {showRadioButtons && (
        <div className="flex flex-col gap-2 mb-5">
          <div className="flex items-center gap-1">
            <Label className="text-sm font-medium text-n-text-neutral">
              As informações de cobrança são as mesmas da coleta/entrega?
            </Label>
            <CustomPopover
              title="Endereço de cobrança"
              description="Se o endereço de cobrança for o mesmo da coleta ou entrega, selecione a opção correspondente para preencher automaticamente."
              iconSize={4}
            />
          </div>
          <RadioGroup
            value={addressSource || ""}
            onValueChange={handleAddressSourceChange}
            className="flex gap-4"
          >
            {coletaCep && (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="coleta" id="coleta" />
                <Label htmlFor="coleta" className="cursor-pointer">
                  Sim, de Coleta
                </Label>
              </div>
            )}
            {entregaCep && (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="entrega" id="entrega" />
                <Label htmlFor="entrega" className="cursor-pointer">
                  Sim, de Entrega
                </Label>
              </div>
            )}
          </RadioGroup>
        </div>
      )}

      <FormField
        control={form.control}
        name="address.cep"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2 mb-2">
              <FormLabel htmlFor="cep">CEP</FormLabel>
              <CustomPopover
                title="Como funciona o CEP?"
                description="Digite seu CEP e clique em 'Pesquisar' para preencher automaticamente o endereço. Você pode editar os campos depois."
                iconSize={4}
              />
            </div>
            <div className="flex gap-1">
              <FormControl>
                <Input
                  id="cep"
                  placeholder="00000-000"
                  {...field}
                  value={formatCEP(field.value || "")}
                  onChange={(e) => {
                    const formatted = formatCEP(e.target.value);
                    field.onChange(formatted);
                  }}
                  maxLength={9}
                  className="flex-1 rounded-r-sm"
                />
              </FormControl>
              <Button
                type="button"
                onClick={handleSearchCep}
                disabled={loading}
                className="whitespace-nowrap rounded-l-sm rounded-r-xl text-sm md:text-base"
              >
                {loading ? <>Buscando...</> : <>Pesquisar</>}
              </Button>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="address.logradouro"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2 mb-2">
              <FormLabel htmlFor="logradouro">Endereço</FormLabel>
              <CustomPopover
                title="O que é Endereço?"
                description="Refere-se ao nome da rua, avenida, praça, etc., onde você reside ou onde deseja receber a correspondência."
                iconSize={4}
              />
            </div>
            <FormControl>
              <Input
                id="logradouro"
                placeholder="Ex: Rua das Flores"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
        <FormField
          control={form.control}
          name="address.numero"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2 mb-2">
                <FormLabel htmlFor="numero">Número</FormLabel>
              </div>
              <FormControl>
                <Input
                  id="numero"
                  placeholder="Ex: 123 ou S/N"
                  {...field}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val) || /^[Ss]\/?[Nn]?$/.test(val)) {
                      field.onChange(val);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address.complemento"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2 mb-2">
                <FormLabel htmlFor="complemento">
                  Complemento (opcional)
                </FormLabel>
                <CustomPopover
                  title="Complemento"
                  description="Informações adicionais como apartamento, bloco, sala, etc. Exemplo: Apto 45, Bloco B."
                  iconSize={4}
                />
              </div>
              <FormControl>
                <Input
                  id="complemento"
                  placeholder="Ex: Apto 45"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="address.bairro"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2 mb-2">
              <FormLabel htmlFor="bairro">Bairro</FormLabel>
            </div>
            <FormControl>
              <Input id="bairro" placeholder="Ex: Centro" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="address.mun_codigo"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2 mb-2">
              <FormLabel htmlFor="municipio">Município</FormLabel>
              <CustomPopover
                title="Seleção de Município"
                description="Selecione o município na lista. Se você pesquisou o CEP, o município será preenchido automaticamente."
                iconSize={4}
              />
            </div>
            <FormControl>
              <FilteredSelectLocation
                value={field.value}
                onChange={field.onChange}
                placeholder="Selecione o município"
                options={locations}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
